import * as CookieConsent from 'vanilla-cookieconsent';

type CookieConsentConfig = Parameters<typeof CookieConsent.run>[0];

const PRIVACY_PATHS = {
  en: '/privacy-policy',
  hu: '/adatvedelmi-tajekoztato',
  ro: '/politica-de-confidentialitate',
} as const;

type Locale = keyof typeof PRIVACY_PATHS;

function translation(locale: Locale) {
  const privacyHref = PRIVACY_PATHS[locale];
  const privacyLink =
    locale === 'hu'
      ? `<a href="${privacyHref}">Adatvédelmi tájékoztató</a>`
      : locale === 'ro'
        ? `<a href="${privacyHref}">Politica de confidențialitate</a>`
        : `<a href="${privacyHref}">Privacy policy</a>`;

  const copy =
    locale === 'hu'
      ? {
          title: 'Adatvédelem és tárolás',
          description:
            'A weboldal a működéshez szükséges adatokat a böngésző helyi tárolójában (localStorage) tartja — jelenleg nem használunk marketing sütiket. A térképekhez az OpenStreetMap szolgáltatását hívjuk meg; ehhez az Ön hozzájárulása szükséges.',
          acceptAll: 'Összes elfogadása',
          acceptNecessary: 'Csak a szükséges',
          showPreferences: 'Beállítások kezelése',
          save: 'Beállítások mentése',
          close: 'Bezárás',
          necessaryTitle: 'Szükséges',
          necessaryDesc:
            'A hozzájárulás megjegyzése (cc_cookie) és a bejelentkezéshez szükséges helyi tárolás.',
          functionalTitle: 'Funkcionális',
          functionalDesc:
            'Nyelv, elrendezés és egyéb felület-beállítások (localStorage). Ezek nélkül az oldal kevésbé használható.',
          thirdPartyTitle: 'Harmadik fél — térképek',
          thirdPartyDesc:
            'OpenStreetMap csempék betöltése a térképek megjelenítéséhez. A szolgáltató saját sütiket helyezhet el.',
        }
      : locale === 'ro'
        ? {
            title: 'Confidențialitate și stocare',
            description:
              'Site-ul păstrează date necesare funcționării în stocarea locală a browserului (localStorage) — nu folosim cookie-uri de marketing. Pentru hărți apelăm OpenStreetMap; este nevoie de consimțământul dvs.',
            acceptAll: 'Acceptă tot',
            acceptNecessary: 'Doar necesare',
            showPreferences: 'Gestionează preferințele',
            save: 'Salvează preferințele',
            close: 'Închide',
            necessaryTitle: 'Necesare',
            necessaryDesc:
              'Memorarea alegerii (cc_cookie) și stocarea locală pentru autentificare.',
            functionalTitle: 'Funcționale',
            functionalDesc:
              'Limbă, aspect și alte preferințe de interfață (localStorage).',
            thirdPartyTitle: 'Terț — hărți',
            thirdPartyDesc:
              'Încărcarea plăcilor OpenStreetMap. Furnizorul poate seta propriile cookie-uri.',
          }
        : {
            title: 'Privacy & storage',
            description:
              'This site stores data needed to work in your browser’s local storage (localStorage) — we do not use marketing cookies today. Maps load tiles from OpenStreetMap; that requires your consent.',
            acceptAll: 'Accept all',
            acceptNecessary: 'Reject non-essential',
            showPreferences: 'Manage preferences',
            save: 'Save preferences',
            close: 'Close',
            necessaryTitle: 'Necessary',
            necessaryDesc:
              'Remembering your choice (cc_cookie) and local storage required to stay signed in.',
            functionalTitle: 'Functional',
            functionalDesc:
              'Language, layout, and other UI preferences (localStorage).',
            thirdPartyTitle: 'Third party — maps',
            thirdPartyDesc:
              'Loading map tiles from OpenStreetMap. The provider may set its own cookies.',
          };

  return {
    consentModal: {
      title: copy.title,
      description: copy.description,
      acceptAllBtn: copy.acceptAll,
      acceptNecessaryBtn: copy.acceptNecessary,
      showPreferencesBtn: copy.showPreferences,
      footer: privacyLink,
    },
    preferencesModal: {
      title: copy.title,
      savePreferencesBtn: copy.save,
      closeIconLabel: copy.close,
      sections: [
        {
          title: copy.necessaryTitle,
          description: copy.necessaryDesc,
          linkedCategory: 'necessary',
        },
        {
          title: copy.functionalTitle,
          description: copy.functionalDesc,
          linkedCategory: 'functional',
        },
        {
          title: copy.thirdPartyTitle,
          description: copy.thirdPartyDesc,
          linkedCategory: 'third_party',
        },
      ],
    },
    categories: {
      necessary: { name: copy.necessaryTitle },
      functional: { name: copy.functionalTitle },
      third_party: { name: copy.thirdPartyTitle },
    },
  };
}

export function createCookieConsentConfig(onConsentChange: () => void): CookieConsentConfig {
  return {
    mode: 'opt-in',
    autoShow: true,
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
      },
    },
    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      functional: {
        enabled: true,
        readOnly: true,
      },
      third_party: {},
    },
    language: {
      default: 'hu',
      translations: {
        en: translation('en'),
        hu: translation('hu'),
        ro: translation('ro'),
      },
    },
    onConsent: onConsentChange,
    onChange: onConsentChange,
  };
}
